import { useEffect, useState } from 'react';
import { useWorkflowsService } from '../contexts/workflows-service';
import { GetWorkflowsQuery, UseWorkflowsHook, WorkflowsService, WorkflowSummary } from '../ports/WorkflowService';

export function createUseWorkflows(
  {
    useWorkflowsService,
  }: {
    useWorkflowsService: () => WorkflowsService | undefined;
  },
): UseWorkflowsHook {
  return function useWorkflows() {
    const workflowsService = useWorkflowsService();

    const [workflows, setWorkflows] = useState<WorkflowSummary[]>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [error, setError] = useState<Error>();
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string>();

    const updateWorkflows = (newWorkflows: WorkflowSummary[]) => {
      setWorkflows(oldWorkflows => {
        const updatedWorkflows = [
          ...newWorkflows,
          ...(oldWorkflows ?? []).filter(({ id }) => !newWorkflows.some(workflow => id === workflow.id)),
        ];
        updatedWorkflows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        return updatedWorkflows;
      });
    };

    const update = (query?: GetWorkflowsQuery) => {
      if (!workflowsService || isLoading) {
        return;
      }
      setLastUpdatedAt(new Date().toISOString());
      setIsLoading(true);
      workflowsService
        .getWorkflows(query ?? { updatedAfter: lastUpdatedAt })
        .then(({ workflows, totalCount }) => {
          updateWorkflows(workflows);
          setTotalCount(totalCount);
        })
        .catch(setError)
        .then(() => setIsLoading(false));
    };

    const loadMore = () => update({ createdBefore: workflows?.[workflows.length - 1]?.createdAt });

    useEffect(
      () => {
        if (!workflows) {
          update({});
        }
      },
      [workflows],
    );

    return {
      workflows,
      isLoading,
      totalCount,
      error,
      loadMore,
      update,
    };
  };
}

export const useWorkflows =  createUseWorkflows({ useWorkflowsService });
